<template>
    <CustomDialog v-model="isVisible" :defaultTitle="isEdit ? $t('api.form.title_edit') : $t('api.form.title_add')" width="600px">
        <template #content>
            <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                <CustomInput
                    :label="$t('api.form.name.label')"
                    v-model="form.name"
                    :placeholder="$t('api.form.name.placeholder')"
                    :required="true"
                    :errorText="errors.name"
                    :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                    customLabelClass="poppinsLight ppv2-text-black"
                />

                <div>
                    <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-1">
                        <label class="poppinsLight ppv2-text-black" :style="`font-size: ${normalizeSize(15, true)}`">
                            {{ $t('api.form.scopes.label') }}
                        </label>
                        <button type="button" class="ppv2-text-xs ppv2-underline ppv2-text-[#7D2DBD]" @click="toggleAllScopes">
                            {{ allScopesSelected ? $t('api.form.scopes.none') : $t('api.form.scopes.all') }}
                        </button>
                    </div>

                    <p class="ppv2-text-xs ppv2-text-gray-500 ppv2-mb-2">{{ $t('api.form.scopes.help') }}</p>

                    <div
                        class="ppv2-max-h-[220px] ppv2-overflow-y-auto ppv2-border ppv2-border-gray-200 ppv2-rounded-lg ppv2-p-3 ppv2-flex ppv2-flex-col ppv2-gap-2"
                    >
                        <label
                            v-for="scope in availableScopes"
                            :key="scope.id"
                            class="ppv2-flex ppv2-items-start ppv2-gap-2 ppv2-cursor-pointer"
                        >
                            <input type="checkbox" :value="scope.id" v-model="form.allowed_scopes" class="ppv2-mt-1" />
                            <span>
                                <code class="ppv2-text-xs ppv2-bg-gray-100 ppv2-px-1 ppv2-rounded">{{ scope.id }}</code>
                                <span class="ppv2-block ppv2-text-xs ppv2-text-gray-500">{{ scope.description }}</span>
                            </span>
                        </label>

                        <span v-if="!availableScopes.length" class="ppv2-text-xs ppv2-text-gray-400">
                            {{ $t('api.form.scopes.empty') }}
                        </span>
                    </div>

                    <p class="ppv2-text-xs ppv2-text-amber-600 ppv2-mt-2" v-if="!form.allowed_scopes.length">
                        {{ $t('api.form.scopes.unrestricted') }}
                    </p>
                </div>

                <div v-if="isEdit" class="ppv2-mt-2">
                    <div class="ppv2-inline-flex ppv2-items-end ppv2-gap-3 ppv2-px-4 ppv2-mb-[20px]">
                        <BaseSwitch
                            :label="$t('api.form.regenerate_secret.label')"
                            v-model="form.regenerate_secret"
                            :boxed="true"
                            variant="warning"
                            :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                            customLabelClass="poppinsLight ppv2-text-black"
                        />
                    </div>
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton variant="outline" @click="cancel" :customHeight="48" customClass="!ppv2-min-w-[160px]">
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton
                variant="primary"
                :loading="isSaving"
                @click="save"
                :customHeight="48"
                customClass="!ppv2-min-w-[160px] !ppv2-bg-[#7D2DBD] hover:!ppv2-bg-[#6b26a3]"
            >
                {{ isEdit ? $t('common.validate') : $t('common.add') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import normalizeSize from '../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'ApplicationForm',
    components: {
        CustomInput,
        BaseButton,
        BaseSwitch,
        CustomDialog,
    },
    props: {
        value: { type: Boolean, default: false },
        applicationData: { type: Object, default: () => null },
        token: { type: String, default: '' },
    },
    data() {
        return {
            isSaving: false,
            form: {
                id: null,
                name: '',
                regenerate_secret: false,
                allowed_scopes: [],
            },
            availableScopes: [],
            errors: {},
            initialForm: null,
        }
    },
    computed: {
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        isEdit() {
            return !!(this.applicationData && this.applicationData.id)
        },
        allScopesSelected() {
            return this.availableScopes.length > 0 && this.form.allowed_scopes.length === this.availableScopes.length
        },
    },
    watch: {
        applicationData: {
            handler(newVal) {
                if (newVal) {
                    this.initForm()
                } else {
                    this.resetForm()
                }
                this.initialForm = JSON.stringify(this.form)
            },
            immediate: true,
        },
        isVisible(newVal) {
            if (newVal && !this.isEdit) {
                this.resetForm()
                this.initialForm = JSON.stringify(this.form)
            }
        },
    },
    mounted() {
        this.fetchScopes()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initForm() {
            this.form = {
                id: this.applicationData.id,
                name: this.applicationData.name || '',
                regenerate_secret: false,
                allowed_scopes: this.applicationData.allowed_scopes || [],
            }
        },
        resetForm() {
            this.form = {
                id: null,
                name: '',
                regenerate_secret: false,
                allowed_scopes: [],
            }
            this.errors = {}
        },
        async fetchScopes() {
            try {
                const { data } = await axios.get('/api/v3/applications/scopes', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.availableScopes = data.data || []
            } catch (error) {
                // La liste vient du serveur ; si elle manque, le formulaire reste utilisable et la
                // clé est simplement créée sans restriction.
                this.availableScopes = []
            }
        },
        toggleAllScopes() {
            this.form.allowed_scopes = this.allScopesSelected ? [] : this.availableScopes.map((scope) => scope.id)
        },
        cancel() {
            this.isVisible = false
            this.$emit('close')
        },
        validateForm() {
            this.errors = {}
            let isValid = true

            if (!this.form.name) {
                this.errors.name = this.$t('api.errors.name_required')
                isValid = false
            }

            return isValid
        },
        async save() {
            if (!this.validateForm()) {
                return
            }

            this.isSaving = true
            try {
                const url = this.isEdit ? `/api/v3/applications/update/${this.form.id}` : '/api/v3/applications/create'
                const method = this.isEdit ? 'put' : 'post'

                const response = await axios[method](url, this.form, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.$toast.success(response.data.message || this.$t('applications.save_success'))
                    this.initialForm = JSON.stringify(this.form)
                    this.$emit('success')
                    this.exit()
                } else {
                    this.$toast.error(response.data.message || this.$t('common.generic_error'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors
                } else if (error.response && error.response.data && error.response.data.message) {
                    this.$toast.error(error.response.data.message)
                } else {
                    this.$toast.error(this.$t('common.save_error'))
                }
            } finally {
                this.isSaving = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
:deep(.v-card) {
    border-radius: 10px !important;
}
</style>
